/* ============================================================================
   GallerySection.tsx — sidebar gauche en mode 'gallery'.
   Affiche : compteur photos, slider auto-rotate, navigation prev/next,
   liste de mini-thumbnails cliquables.

   Le canvas central est l'autorite sur l'index courant — ce composant
   communique via le store canvasStore (galleryIndex non stocke ici, on lit
   l'event depuis le canvas via window event ou via prop drilling). MVP :
   on partage via custom event sur window.

   Pour rester simple, l'index courant est gere via window CustomEvent
   `marthe-gallery-set-index` (declenche par le canvas / les boutons).
   ============================================================================ */

import { useCallback, useEffect, useState } from 'react';
import {
  useCanvasParams,
  type GalleryAutoRotateMs,
} from '../../lib/canvasStore';
import { Section } from './Section';
import { RadioGroup, type RadioOption } from './RadioGroup';
import { usePolledMessages } from '../../lib/usePolledMessages';
import { dispatchGalleryEvent, useGalleryIndex } from '../../lib/galleryBus';

const AUTO_ROTATE_OPTIONS: RadioOption<string>[] = [
  { value: '3000', label: '3s' },
  { value: '6000', label: '6s' },
  { value: '12000', label: '12s' },
  { value: '0', label: 'off' },
];

export function GallerySection() {
  const [params, dispatch] = useCanvasParams();
  const { messages } = usePolledMessages(5000);
  const photos = messages.filter((m) => m.photoUrl);
  const total = photos.length;

  const [index, setIndex] = useState<number>(0);
  useGalleryIndex(setIndex);

  // Clamp si la liste change
  useEffect(() => {
    if (total === 0) {
      if (index !== 0) setIndex(0);
      return;
    }
    if (index >= total) {
      setIndex(0);
      dispatchGalleryEvent('set', 0);
    }
  }, [total, index]);

  const goPrev = useCallback(() => {
    if (total === 0) return;
    dispatchGalleryEvent('prev');
  }, [total]);

  const goNext = useCallback(() => {
    if (total === 0) return;
    dispatchGalleryEvent('next');
  }, [total]);

  const goTo = useCallback(
    (i: number) => {
      if (total === 0) return;
      dispatchGalleryEvent('set', i);
    },
    [total],
  );

  const onAutoRotate = useCallback(
    (v: string) => {
      const ms = Number(v) as GalleryAutoRotateMs;
      dispatch({ type: 'set', key: 'galleryAutoRotateMs', value: ms });
    },
    [dispatch],
  );

  const currentValue = String(params.galleryAutoRotateMs);

  return (
    <Section title="gallery" hint="photos">
      <p className="marthe-generate__subtitle" style={{ margin: 0 }}>
        photos envoyees via /send → carousel glyph art
      </p>

      <div className="controls-row" style={{ marginTop: 'var(--sp-2)' }}>
        <span className="controls-label">{total} photo{total > 1 ? 's' : ''} reçue{total > 1 ? 's' : ''}</span>
        <span className="controls-value" data-testid="gallery-position">
          {total > 0 ? `${index + 1}/${total}` : '–'}
        </span>
      </div>

      <div className="controls-sublabel">auto-rotate</div>
      <RadioGroup
        options={AUTO_ROTATE_OPTIONS}
        value={currentValue}
        onChange={onAutoRotate}
      />

      <div
        style={{
          display: 'flex',
          gap: 0,
          marginTop: 'var(--sp-2)',
        }}
      >
        <button
          type="button"
          className="canvas-action"
          onClick={goPrev}
          disabled={total === 0}
          data-testid="gallery-prev"
          aria-label="photo precedente"
          style={{ flex: 1 }}
        >
          ← prev
        </button>
        <button
          type="button"
          className="canvas-action"
          onClick={goNext}
          disabled={total === 0}
          data-testid="gallery-next"
          aria-label="photo suivante"
          style={{ flex: 1 }}
        >
          next →
        </button>
      </div>

      {total === 0 ? (
        <p
          style={{
            margin: 'var(--sp-3) 0 0',
            fontSize: 11,
            color: 'var(--stone)',
            lineHeight: 1.4,
          }}
        >
          aucune photo reçue.
          <br />
          partage le lien /send pour recevoir des photos.
        </p>
      ) : (
        <div
          style={{
            marginTop: 'var(--sp-2)',
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 4,
          }}
          data-testid="gallery-thumbs"
        >
          {photos.map((m, i) => (
            <button
              key={m.id}
              type="button"
              onClick={() => goTo(i)}
              data-testid={`gallery-thumb-${i}`}
              aria-label={`photo ${i + 1} : ${m.title}`}
              aria-current={i === index ? 'true' : undefined}
              style={{
                appearance: 'none',
                border: i === index ? '1px solid var(--ink)' : '1px solid var(--border)',
                background: 'transparent',
                padding: 0,
                cursor: 'pointer',
                aspectRatio: '1 / 1',
                overflow: 'hidden',
                opacity: i === index ? 1 : 0.6,
                transition: 'opacity 80ms ease, border-color 80ms ease',
              }}
            >
              {m.photoUrl ? (
                <img
                  src={m.photoUrl}
                  alt=""
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                  }}
                />
              ) : null}
            </button>
          ))}
        </div>
      )}
    </Section>
  );
}
